import React, { useEffect, useState, useContext } from 'react';
import '../../styles/admin/admin-team-page.scss';
import { Link, useNavigate } from 'react-router-dom';
import { baseURL } from '../../Constants';
import { AuthContext } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faUsers,
  faSpinner,
  faExclamationTriangle,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';

// Define interfaces based on the C# models
interface Account {
  steamID: string;
  name?: string;
  dateOfBirth?: string;
  playerName: string;
  twitter?: string;
  role?: string;
  team?: Team | null;
  teamSeason?: number | null;  // Which season the team is from
  currentSeason?: number;       // Current season context
  isAdmin?: boolean;
  passwordHash?: string;
  age?: number;
}

interface Team {
  id: string;
  name: string;
  logo: string | null;
  players: Account[];
  currentSeason?: number;
  currentDivision?: string;
  currentSeed?: string;
  currentDescription?: string | null;
  currentCoach?: Account | null;
}

interface TeamSeason {
  season: number;
  division: string;
  seed: string;
  players: Account[];
}

const AdminTeamPage: React.FC = () => {
  // A better fallback image - using a data URI for a simple placeholder
  const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

  const navigate = useNavigate();
  const { token, isAdmin } = useContext(AuthContext);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [newTeamName, setNewTeamName] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [editingPlayers, setIsEditingPlayers] = useState<boolean>(false);
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null);
  const [availablePlayers, setAvailablePlayers] = useState<Account[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [bulkActionOpen, setBulkActionOpen] = useState<boolean>(false);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
  const [seasonTeams, setSeasonTeams] = useState<Team[]>([]);
  const [isAddingToSeason, setIsAddingToSeason] = useState<boolean>(false);
  const [addSeasonData, setAddSeasonData] = useState<{ teamId: string; season: number; division: string; seed: string }>({ teamId: '', season: 1, division: 'Main', seed: '1' });
  const [teamSeasons, setTeamSeasons] = useState<{ [teamId: string]: number[] }>({});
  const [isEditingDescription, setIsEditingDescription] = useState<boolean>(false);
  const [descriptionTeamId, setDescriptionTeamId] = useState<string | null>(null);
  const [descriptionSeason, setDescriptionSeason] = useState<number | null>(null);
  const [teamDescription, setTeamDescription] = useState<string>('');
  const [isManagingCoach, setIsManagingCoach] = useState<boolean>(false);
  const [coachTeamId, setCoachTeamId] = useState<string | null>(null);
  const [coachSearchTerm, setCoachSearchTerm] = useState<string>('');

  // Check if user is an admin before allowing access
  useEffect(() => {
    if (!token || !isAdmin) {
      navigate('/admin/login');
    }
  }, [token, isAdmin, navigate]);

  // Fetch teams data
  const fetchTeams = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Use the specific season endpoint if a season is selected
      const url = selectedSeason
        ? `${baseURL}/Team/season/${selectedSeason}`
        : `${baseURL}/Team`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setTeams(data);
      setSeasonTeams(data);

      // Fetch team seasons for each team
      const seasonPromises = data.map((team: Team) =>
        fetch(`${baseURL}/Team/${team.id}/seasons`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => res.ok ? res.json() : [])
      );

      const allSeasons = await Promise.all(seasonPromises);
      const seasonMap: { [teamId: string]: number[] } = {};
      data.forEach((team: Team, index: number) => {
        seasonMap[team.id] = allSeasons[index];
      });
      setTeamSeasons(seasonMap);

      // Extract unique seasons
      const uniqueSeasons = new Set<number>();
      allSeasons.forEach(seasons => {
        seasons.forEach((s: number) => uniqueSeasons.add(s));
      });
      setAvailableSeasons(Array.from(uniqueSeasons).sort((a, b) => b - a));

      // Fetch full team data with season to get coach information
      const teamsWithCoachPromises = data.map((team: Team) => {
        const season = selectedSeason || team.currentSeason;
        if (season) {
          return fetch(`${baseURL}/Team/${team.id}?season=${season}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(res => res.ok ? res.json() : null);
        }
        return Promise.resolve(null);
      });

      const teamsWithCoachData = await Promise.all(teamsWithCoachPromises);

      // Update teams with coach data
      const updatedTeams = data.map((team: Team, index: number) => {
        const teamWithCoach = teamsWithCoachData[index];
        if (teamWithCoach && teamWithCoach.currentCoach) {
          return { ...team, currentCoach: teamWithCoach.currentCoach };
        }
        return team;
      });

      setTeams(updatedTeams);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch available players (players with no team)
  const fetchAvailablePlayers = async () => {
    setIsLoadingPlayers(true);
    try {
      // Since there's no specific /available endpoint, we'll fetch all accounts
      // and filter based on season-aware logic
      const response = await fetch(`${baseURL}/Account`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();

      // Filter to include:
      // 1. Accounts without a team (never been on a team)
      // 2. Accounts whose team is from a PREVIOUS season (can coach new season)
      // This allows Season 2 players to be coaches for Season 3 teams
      const availableAccounts = data.filter((account: Account) => {
        // No team at all - definitely available
        if (!account.team) return true;

        // Has a team from a previous season - available as coach
        // (teamSeason < currentSeason means their team is from an older season)
        if (account.teamSeason && account.currentSeason && account.teamSeason < account.currentSeason) {
          return true;
        }

        // Has a team from current season - not available
        return false;
      });

      setAvailablePlayers(availableAccounts);
    } catch (err) {
      console.error('Error fetching available players:', err);
      setError('Failed to load available players. Please try again later.');
    } finally {
      setIsLoadingPlayers(false);
    }
  };

  useEffect(() => {
    fetchTeams();

    // Set page title
    document.title = 'Manage Teams - Northern Championships Series Admin';
  }, [selectedSeason]);

  // Handle single team selection
  const handleTeamSelect = (teamId: string) => {
    if (selectedTeams.includes(teamId)) {
      setSelectedTeams(selectedTeams.filter(id => id !== teamId));
    } else {
      setSelectedTeams([...selectedTeams, teamId]);
    }
  };

  // Handle select all teams
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTeams([]);
    } else {
      setSelectedTeams(teams.map(team => team.id));
    }
    setSelectAll(!selectAll);
  };

  // Start editing a team
  const handleEditClick = (team: Team) => {
    setEditingTeam(team);
    setNewTeamName(team.name);
    
    // Only set a preview URL if we know the team has a logo
    if (team.logo) {
      // Add a cache-busting parameter to force a fresh image
      setLogoPreview(`${baseURL}/Team/${team.id}/logo?t=${new Date().getTime()}`);
    } else {
      setLogoPreview(null);
    }
    
    setIsEditing(true);
  };

  // Handle logo file selection
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check if file is a PNG
      if (file.type !== 'image/png') {
        setError('Only PNG files are accepted for team logos.');
        return;
      }
      
      setLogoFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload team logo
  const uploadTeamLogo = async (teamId: string): Promise<boolean> => {
    if (!logoFile) return true; // Skip if no new logo
    
    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      
      
      const response = await fetch(`${baseURL}/Team/${teamId}/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const responseText = await response.text();
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      return true;
    } catch (err) {
      console.error('Error uploading team logo:', err);
      setError('Failed to upload team logo. Team was updated but logo was not changed.');
      return false;
    }
  };

  // Save edited team
  const handleSaveEdit = async () => {
    if (!editingTeam || !newTeamName.trim()) return;

    try {
      const updatedTeam = { ...editingTeam, name: newTeamName };
      
      // First, update the team name
      const response = await fetch(`${baseURL}/Team/${editingTeam.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedTeam),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      
      // Update local state for the name change
      setTeams(teams.map(team => 
        team.id === editingTeam.id ? { ...team, name: newTeamName } : team
      ));
      
      // Upload logo if a new one was selected
      if (logoFile) {
        const logoUploaded = await uploadTeamLogo(editingTeam.id);
        
        if (logoUploaded) {
          // If successful, refresh teams to get the updated logo
          await fetchTeams();
        }
      }
      
      // Reset editing state
      setIsEditing(false);
      setEditingTeam(null);
      setLogoFile(null);
      setLogoPreview(null);
    } catch (err) {
      console.error('Error updating team:', err);
      setError('Failed to update team. Please try again later.');
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingTeam(null);
    setLogoFile(null);
    setLogoPreview(null);
  };

  // Delete a single team
  const handleDeleteTeam = async (teamId: string) => {
    try {
      const response = await fetch(`${baseURL}/Team/${teamId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Remove from selected teams if it was selected
      if (selectedTeams.includes(teamId)) {
        setSelectedTeams(selectedTeams.filter(id => id !== teamId));
      }

      // Update local state
      setTeams(teams.filter(team => team.id !== teamId));
      setConfirmingDelete(null);
    } catch (err) {
      console.error('Error deleting team:', err);
      setError('Failed to delete team. Please try again later.');
    }
  };

  // Delete multiple teams (bulk action)
  const handleBulkDelete = async () => {
    if (selectedTeams.length === 0) return;

    try {
      // Create an array of promises for each delete operation
      const deletePromises = selectedTeams.map(teamId => 
        fetch(`${baseURL}/Team/${teamId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      );

      // Wait for all delete operations to complete
      const results = await Promise.allSettled(deletePromises);
      
      // Check for any failed operations
      const failedOperations = results.filter(result => result.status === 'rejected');
      
      if (failedOperations.length > 0) {
        console.error(`${failedOperations.length} delete operations failed`);
        setError(`Failed to delete ${failedOperations.length} teams.`);
      }

      // Refresh the teams list
      fetchTeams();
      // Clear selection
      setSelectedTeams([]);
      setSelectAll(false);
      // Close bulk action dropdown
      setBulkActionOpen(false);
    } catch (err) {
      console.error('Error performing bulk delete:', err);
      setError('Failed to delete teams. Please try again later.');
    }
  };

  // Open player management modal for a team
  const handleManagePlayers = (teamId: string) => {
    const team = teams.find(t => t.id === teamId);

    // If no specific season is selected and team has a currentSeason, use it
    // Otherwise require season selection first
    if (!selectedSeason && !team?.currentSeason) {
      setError('Please select a specific season first to manage players.');
      return;
    }

    setCurrentTeamId(teamId);
    setIsEditingPlayers(true);
    fetchAvailablePlayers();
  };

  // Close player management modal
  const handleClosePlayerManagement = () => {
    setIsEditingPlayers(false);
    setCurrentTeamId(null);
    setSearchTerm('');
  };

  // Open description editor modal
  const handleEditDescription = async (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    const season = selectedSeason || team?.currentSeason;

    if (!season) {
      setError('Please select a season first to edit the description.');
      return;
    }

    setDescriptionTeamId(teamId);
    setDescriptionSeason(season);

    // Fetch the team data with season to get the current description
    try {
      const response = await fetch(`${baseURL}/Team/${teamId}?season=${season}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const teamData = await response.json();
        setTeamDescription(teamData.currentDescription || '');
      } else {
        setTeamDescription(team?.currentDescription || '');
      }
    } catch (err) {
      console.error('Error fetching team description:', err);
      setTeamDescription(team?.currentDescription || '');
    }

    setIsEditingDescription(true);
  };

  // Close description editor modal
  const handleCloseDescriptionEditor = () => {
    setIsEditingDescription(false);
    setDescriptionTeamId(null);
    setDescriptionSeason(null);
    setTeamDescription('');
  };

  // Save team season description
  const handleSaveDescription = async () => {
    if (!descriptionTeamId || !descriptionSeason) return;

    try {
      const response = await fetch(
        `${baseURL}/Team/${descriptionTeamId}/seasons/${descriptionSeason}/description`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(teamDescription || null)
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Refresh teams to show updated description
      await fetchTeams();
      handleCloseDescriptionEditor();
    } catch (err) {
      console.error('Error saving description:', err);
      setError('Failed to save description. Please try again later.');
    }
  };

  // Open coach management modal
  const handleManageCoach = async (teamId: string) => {
    setCoachTeamId(teamId);
    setIsManagingCoach(true);
    setCoachSearchTerm('');

    // Fetch fresh team data with season to get current coach
    try {
      const team = teams.find(t => t.id === teamId);
      const season = selectedSeason || team?.currentSeason;

      if (season) {
        const response = await fetch(`${baseURL}/Team/${teamId}?season=${season}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const freshTeamData = await response.json();
          // Update this team in the teams array
          setTeams(prevTeams =>
            prevTeams.map(t => t.id === teamId ? { ...t, currentCoach: freshTeamData.currentCoach } : t)
          );
        }
      }
    } catch (error) {
      console.error('Error fetching team coach data:', error);
    }

    if (!availablePlayers || availablePlayers.length === 0) {
      fetchAvailablePlayers();
    }
  };

  // Close coach management modal
  const handleCloseCoachManagement = () => {
    setIsManagingCoach(false);
    setCoachTeamId(null);
    setCoachSearchTerm('');
  };

  // Assign coach to team
  const handleAssignCoach = async (coachSteamId: string) => {
    if (!coachTeamId) return;

    try {
      const team = teams.find(t => t.id === coachTeamId);
      const season = team?.currentSeason || selectedSeason;

      if (!season) {
        setError('Please select a season first.');
        return;
      }

      const response = await fetch(
        `${baseURL}/Team/${coachTeamId}/seasons/${season}/coach`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(coachSteamId)
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Refresh teams to show updated coach
      await fetchTeams();
      handleCloseCoachManagement();
    } catch (err) {
      console.error('Error assigning coach:', err);
      setError('Failed to assign coach. Please try again later.');
    }
  };

  // Remove coach from team
  const handleRemoveCoach = async () => {
    if (!coachTeamId) return;

    try {
      const team = teams.find(t => t.id === coachTeamId);
      const season = team?.currentSeason || selectedSeason;

      if (!season) {
        setError('Please select a season first.');
        return;
      }

      const response = await fetch(
        `${baseURL}/Team/${coachTeamId}/seasons/${season}/coach`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Refresh teams to show updated coach
      await fetchTeams();
    } catch (err) {
      console.error('Error removing coach:', err);
      setError('Failed to remove coach. Please try again later.');
    }
  };

  // Add player to team
  const handleAddPlayer = async (teamId: string, steamId: string) => {
    try {
      const team = teams.find(t => t.id === teamId);
      const season = team?.currentSeason || selectedSeason;

      if (!season) {
        setError('Please select a season first.');
        return;
      }

      const response = await fetch(`${baseURL}/Team/${teamId}/seasons/${season}/roster`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify([...(team?.players.map(p => p.steamID) || []), steamId])
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Refresh teams data after adding a player
      await fetchTeams();

      // Remove the player from available players list
      setAvailablePlayers(availablePlayers.filter(player => player.steamID !== steamId));
    } catch (err) {
      console.error('Error adding player to team:', err);
      setError('Failed to add player to team. Please try again later.');
    }
  };

  // Remove player from team
  const handleRemovePlayer = async (steamId: string) => {
    try {
      const team = teams.find(t => t.players.some(p => p.steamID === steamId));
      if (!team) return;

      const season = team.currentSeason || selectedSeason;
      if (!season) {
        setError('Please select a season first.');
        return;
      }

      const response = await fetch(`${baseURL}/Team/${team.id}/seasons/${season}/roster`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(team.players.filter(p => p.steamID !== steamId).map(p => p.steamID))
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Refresh teams data after removing a player
      await fetchTeams();

      // Refresh available players list
      await fetchAvailablePlayers();
    } catch (err) {
      console.error('Error removing player from team:', err);
      setError('Failed to remove player from team. Please try again later.');
    }
  };

  // Add team to season
  const handleAddTeamToSeason = async () => {
    try {
      const response = await fetch(`${baseURL}/Team/${addSeasonData.teamId}/seasons/${addSeasonData.season}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          division: addSeasonData.division,
          seed: addSeasonData.seed
        })
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Refresh teams data
      await fetchTeams();
      setIsAddingToSeason(false);
      setAddSeasonData({ teamId: '', season: 1, division: 'Main', seed: '1' });
    } catch (err) {
      console.error('Error adding team to season:', err);
      setError('Failed to add team to season. Please try again later.');
    }
  };

  return (
    <div className="admin-team-page">
      <div className="container">
        <div className="admin-header">
          <h1>Manage Teams</h1>
          <Link to="/admin/teams/create" className="btn-primary">
            <FontAwesomeIcon icon={faPlus} /> ADD NEW TEAM
          </Link>
        </div>

        <div className="season-filter-bar">
          <div className="filter-left">
            <label htmlFor="season-filter">Filter by Season:</label>
            <select
              id="season-filter"
              value={selectedSeason || ''}
              onChange={(e) => setSelectedSeason(e.target.value ? parseInt(e.target.value) : null)}
              className="season-select"
            >
              <option value="">All Seasons</option>
              {availableSeasons.map(season => (
                <option key={season} value={season}>Season {season}</option>
              ))}
            </select>
            {!selectedSeason && (
              <span style={{ marginLeft: '10px', color: '#ffc107', fontSize: '0.9em' }}>
                ⚠️ Select a season to manage player rosters
              </span>
            )}
          </div>
          <button
            className="btn-add-to-season"
            onClick={() => setIsAddingToSeason(true)}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Team to Season
          </button>
        </div>

        {error && (
          <div className="alert alert-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}

        <div className="teams-panel">
          <div className="panel-header">
            <div className="bulk-actions">
              <div className="select-all">
                <input 
                  type="checkbox" 
                  id="select-all" 
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
                <label htmlFor="select-all">Select All</label>
              </div>
              
              {selectedTeams.length > 0 && (
                <div className="bulk-actions-dropdown">
                  <button 
                    className="bulk-actions-button"
                    onClick={() => setBulkActionOpen(!bulkActionOpen)}
                  >
                    Bulk Actions ({selectedTeams.length})
                  </button>
                  {bulkActionOpen && (
                    <div className="dropdown-menu">
                      <button 
                        className="dropdown-item danger"
                        onClick={handleBulkDelete}
                      >
                        Delete Selected
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="table-header">
              <div className="header-select"></div>
              <div className="header-logo">LOGO</div>
              <div className="header-name">TEAM NAME</div>
              <div className="header-season">SEASON</div>
              <div className="header-division">DIVISION</div>
              <div className="header-seed">SEED</div>
              <div className="header-players">PLAYERS</div>
              <div className="header-coach">COACH</div>
              <div className="header-actions">ACTIONS</div>
            </div>
          </div>

          {isLoading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
              <p>Loading teams...</p>
            </div>
          ) : teams.length === 0 ? (
            <div className="no-teams">
              <p>No teams found. Click "Add New Team" to add one.</p>
            </div>
          ) : (
            <div className="teams-table">
              {teams.map(team => (
                <div key={team.id} className={`team-row ${selectedTeams.includes(team.id) ? 'selected' : ''}`}>
                  <div className="team-select">
                    <input 
                      type="checkbox" 
                      checked={selectedTeams.includes(team.id)}
                      onChange={() => handleTeamSelect(team.id)}
                    />
                  </div>
                  <div className="team-logo">
                    {isEditing && editingTeam?.id === team.id ? (
                      <div className="logo-preview">
                        <img 
                          src={logoPreview || placeholderImage} 
                          alt="Team logo preview" 
                        />
                      </div>
                    ) : (
                  <img
                    src={`${baseURL}/Team/${team.id}/logo`}
                    alt={`${team.name} logo`}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = placeholderImage;
                    }}
                      />
                    )}
                  </div>
                  <div className="team-name">
                    {isEditing && editingTeam?.id === team.id ? (
                      <input
                        type="text"
                        value={newTeamName}
                        onChange={(e) => setNewTeamName(e.target.value)}
                        className="edit-name-input"
                        placeholder="Team name"
                      />
                    ) : (
                      team.name
                    )}
                  </div>
                  <div className="team-season">
                    {team.currentSeason || '-'}
                  </div>
                  <div className="team-division">
                    {team.currentDivision || '-'}
                  </div>
                  <div className="team-seed">
                    {team.currentSeed || '-'}
                  </div>
                  <div className="team-players">
                    {team.players.length} players
                  </div>
                  <div className="team-coach">
                    {team.currentCoach ? (
                      <span className="coach-name" title={team.currentCoach.playerName}>
                        {team.currentCoach.playerName}
                      </span>
                    ) : (
                      <span className="no-coach">-</span>
                    )}
                  </div>
                  <div className="team-actions">
                    {isEditing && editingTeam?.id === team.id ? (
                      <div className="edit-actions">
                        <div className="logo-upload">
                          <label htmlFor={`logo-upload-${team.id}`} className="btn btn-upload" title="PNG files only">
                            Logo
                          </label>
                          <input
                            id={`logo-upload-${team.id}`}
                            type="file"
                            accept="image/png"
                            onChange={handleLogoChange}
                            className="file-input"
                          />
                        </div>
                        <button 
                          className="btn btn-save"
                          onClick={handleSaveEdit}
                        >
                          Save
                        </button>
                        <button 
                          className="btn btn-cancel"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <button 
                          className="action-link edit"
                          onClick={() => handleEditClick(team)}
                          title="Edit team"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className="action-link players"
                          onClick={() => handleManagePlayers(team.id)}
                          title="Manage players"
                        >
                          <FontAwesomeIcon icon={faUsers} />
                        </button>
                        <button
                          className="action-link coach"
                          onClick={() => handleManageCoach(team.id)}
                          title="Manage coach"
                        >
                          <span style={{ fontSize: '14px', fontWeight: 'bold' }}>C</span>
                        </button>
                        <button
                          className="action-link description"
                          onClick={() => handleEditDescription(team.id)}
                          title="Edit team description"
                        >
                          <FontAwesomeIcon icon={faFileAlt} />
                        </button>
                        {confirmingDelete === team.id ? (
                          <div className="delete-confirmation">
                            <span>Are you sure?</span>
                            <button 
                              className="btn-confirm-delete"
                              onClick={() => handleDeleteTeam(team.id)}
                            >
                              Yes
                            </button>
                            <button 
                              className="btn-cancel-delete"
                              onClick={() => setConfirmingDelete(null)}
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button 
                            className="action-link delete"
                            onClick={() => setConfirmingDelete(team.id)}
                            title="Delete team"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Add Team to Season Modal */}
        {isAddingToSeason && (
          <div className="modal-overlay">
            <div className="add-season-modal">
              <div className="modal-header">
                <h2>Add Team to Season</h2>
                <button className="close-btn" onClick={() => setIsAddingToSeason(false)}>×</button>
              </div>
              <div className="modal-content">
                <div className="form-group">
                  <label htmlFor="season-team">Team:</label>
                  <select
                    id="season-team"
                    value={addSeasonData.teamId}
                    onChange={(e) => setAddSeasonData({...addSeasonData, teamId: e.target.value})}
                  >
                    <option value="">Select a team...</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="season-number">Season:</label>
                  <input
                    id="season-number"
                    type="number"
                    value={addSeasonData.season}
                    onChange={(e) => setAddSeasonData({...addSeasonData, season: parseInt(e.target.value)})}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="season-division">Division:</label>
                  <input
                    id="season-division"
                    type="text"
                    value={addSeasonData.division}
                    onChange={(e) => setAddSeasonData({...addSeasonData, division: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="season-seed">Seed:</label>
                  <input
                    id="season-seed"
                    type="text"
                    value={addSeasonData.seed}
                    onChange={(e) => setAddSeasonData({...addSeasonData, seed: e.target.value})}
                  />
                </div>
                <div className="modal-actions">
                  <button className="btn-primary" onClick={handleAddTeamToSeason}>Add to Season</button>
                  <button className="btn-cancel" onClick={() => setIsAddingToSeason(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Player Management Modal */}
        {editingPlayers && currentTeamId && (
          <div className="modal-overlay">
            <div className="player-management-modal">
              <div className="modal-header">
                <h2>
                  Manage Team Players
                  {(() => {
                    const team = teams.find(t => t.id === currentTeamId);
                    const season = selectedSeason || team?.currentSeason;
                    return season ? ` - Season ${season}` : '';
                  })()}
                </h2>
                <button className="close-btn" onClick={handleClosePlayerManagement}>×</button>
              </div>
              
              <div className="modal-content">
                {!selectedSeason && (
                  <div className="season-notice" style={{
                    padding: '12px',
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    borderLeft: '4px solid #ffc107',
                    marginBottom: '16px',
                    borderRadius: '4px'
                  }}>
                    <strong>Note:</strong> Managing players for Season {teams.find(t => t.id === currentTeamId)?.currentSeason || 'current'}.
                    Select a specific season from the filter to manage players for that season.
                  </div>
                )}
                <div className="current-players-section">
                  <h3>Current Team Players</h3>
                  {teams.find(team => team.id === currentTeamId)?.players.length === 0 ? (
                    <p className="no-players">No players in this team.</p>
                  ) : (
                    <div className="current-players-list">
                      {teams.find(team => team.id === currentTeamId)?.players.map(player => (
                        <div key={player.steamID} className="player-card">
                          <div className="player-avatar">
                            <img 
                              src={`${baseURL}/Account/${player.steamID}/avatar?t=${new Date().getTime()}`} 
                              alt={`${player.playerName}'s avatar`}
                              onError={(e) => {
                                // If the player avatar fails to load, use our placeholder
                                (e.target as HTMLImageElement).src = placeholderImage;
                              }}
                            />
                          </div>
                          <div className="player-info">
                            <div className="player-name">{player.playerName}</div>
                            {player.name && <div className="player-real-name">{player.name}</div>}
                            {player.role && <div className="player-role">{player.role}</div>}
                          </div>
                          <button 
                            className="remove-player-btn"
                            onClick={() => handleRemovePlayer(player.steamID)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="available-players-section">
                  <h3>Available Players</h3>
                  <div className="search-box">
                    <input
                      type="text"
                      placeholder="Search players..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  
                  {isLoadingPlayers ? (
                    <div className="loading-players">
                      <div className="loading-spinner"></div>
                      <p>Loading players...</p>
                    </div>
                  ) : availablePlayers.length === 0 ? (
                    <p className="no-players">No available players found.</p>
                  ) : (
                    <div className="available-players-list">
                      {availablePlayers
                        .filter(player => 
                          player.playerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (player.name && player.name.toLowerCase().includes(searchTerm.toLowerCase()))
                        )
                        .map(player => (
                          <div key={player.steamID} className="player-card">
                            <div className="player-avatar">
                              <img 
                                src={`${baseURL}/Account/${player.steamID}/avatar?t=${new Date().getTime()}`} 
                                alt={`${player.playerName}'s avatar`}
                                onError={(e) => {
                                  // If the player avatar fails to load, use our placeholder
                                  (e.target as HTMLImageElement).src = placeholderImage;
                                }}
                              />
                            </div>
                            <div className="player-info">
                              <div className="player-name">{player.playerName}</div>
                              {player.name && <div className="player-real-name">{player.name}</div>}
                              {player.role && <div className="player-role">{player.role}</div>}
                            </div>
                            <button 
                              className="add-player-btn"
                              onClick={() => handleAddPlayer(currentTeamId, player.steamID)}
                            >
                              Add
                            </button>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Description Editor Modal */}
        {isEditingDescription && descriptionTeamId && descriptionSeason && (
          <div className="modal-overlay">
            <div className="description-editor-modal">
              <div className="modal-header">
                <h2>
                  Edit Team Description
                  {(() => {
                    const team = teams.find(t => t.id === descriptionTeamId);
                    return team ? ` - ${team.name} (Season ${descriptionSeason})` : '';
                  })()}
                </h2>
                <button className="close-btn" onClick={handleCloseDescriptionEditor}>×</button>
              </div>

              <div className="modal-content">
                <div className="form-group">
                  <label>Team Description (for Season {descriptionSeason}):</label>
                  <textarea
                    className="description-textarea"
                    rows={8}
                    value={teamDescription}
                    onChange={(e) => setTeamDescription(e.target.value)}
                    placeholder="Enter a custom description for this team in this season. Leave empty to use the default description."
                  />
                  <p className="help-text">
                    This description will be displayed on the team's public page for Season {descriptionSeason}.
                    If left empty, a default description will be shown instead.
                  </p>
                </div>

                <div className="modal-actions">
                  <button className="btn-primary" onClick={handleSaveDescription}>
                    Save Description
                  </button>
                  <button className="btn-cancel" onClick={handleCloseDescriptionEditor}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isManagingCoach && coachTeamId && (
          <div className="modal-overlay">
            <div className="coach-management-modal">
              <div className="modal-header">
                <h2>
                  Manage Team Coach
                  {(() => {
                    const team = teams.find(t => t.id === coachTeamId);
                    const season = selectedSeason || team?.currentSeason;
                    return season ? ` - Season ${season}` : '';
                  })()}
                </h2>
                <button className="close-btn" onClick={handleCloseCoachManagement}>×</button>
              </div>

              <div className="modal-content">
                {!selectedSeason && (
                  <div className="season-notice" style={{
                    padding: '12px',
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    borderLeft: '4px solid #ffc107',
                    marginBottom: '16px',
                    borderRadius: '4px'
                  }}>
                    <strong>Note:</strong> Managing coach for Season {teams.find(t => t.id === coachTeamId)?.currentSeason || 'current'}.
                    Select a specific season from the filter to manage coach for that season.
                  </div>
                )}

                <div className="current-coach-section">
                  <h3>Current Coach</h3>
                  {(() => {
                    const team = teams.find(t => t.id === coachTeamId);
                    const coach = team?.currentCoach;

                    if (!coach) {
                      return <p className="no-coach">No coach assigned.</p>;
                    }

                    return (
                      <div className="coach-card">
                        <div className="player-avatar">
                          <img
                            src={`${baseURL}/Account/${coach.steamID}/avatar?t=${new Date().getTime()}`}
                            alt={`${coach.playerName}'s avatar`}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = placeholderImage;
                            }}
                          />
                        </div>
                        <div className="player-info">
                          <div className="player-name">{coach.playerName}</div>
                          {coach.name && <div className="player-real-name">{coach.name}</div>}
                          <div className="coach-badge-text">Coach</div>
                        </div>
                        <button
                          className="remove-coach-btn"
                          onClick={handleRemoveCoach}
                        >
                          Remove Coach
                        </button>
                      </div>
                    );
                  })()}
                </div>

                <div className="available-coaches-section">
                  <h3>Assign Coach</h3>
                  <div className="search-box">
                    <input
                      type="text"
                      placeholder="Search players..."
                      value={coachSearchTerm}
                      onChange={(e) => setCoachSearchTerm(e.target.value)}
                    />
                  </div>

                  {isLoadingPlayers ? (
                    <div className="loading-players">
                      <div className="loading-spinner"></div>
                      <p>Loading players...</p>
                    </div>
                  ) : availablePlayers.length === 0 ? (
                    <p className="no-players">No available players found.</p>
                  ) : (
                    <div className="available-players-list">
                      {availablePlayers
                        .filter(player =>
                          player.playerName.toLowerCase().includes(coachSearchTerm.toLowerCase()) ||
                          (player.name && player.name.toLowerCase().includes(coachSearchTerm.toLowerCase()))
                        )
                        .map(player => (
                          <div key={player.steamID} className="player-card">
                            <div className="player-avatar">
                              <img
                                src={`${baseURL}/Account/${player.steamID}/avatar?t=${new Date().getTime()}`}
                                alt={`${player.playerName}'s avatar`}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = placeholderImage;
                                }}
                              />
                            </div>
                            <div className="player-info">
                              <div className="player-name">{player.playerName}</div>
                              {player.name && <div className="player-real-name">{player.name}</div>}
                            </div>
                            <button
                              className="assign-coach-btn"
                              onClick={() => handleAssignCoach(player.steamID)}
                            >
                              Assign as Coach
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTeamPage;